import { Icon } from '@ps/isp-web-component-library';
import { ChevronMediumLeft } from '@ps/isp-web-component-library/icons/ChevronMediumLeft';
import type { TranslationTag } from '@ps/web-core-intl';
import { Link } from '@ps/web-shared-components';
import { type IspSportsAlias, isIspSportsAlias } from '@ps/web-sports-config';
import type { FC } from 'react';
import React from 'react';
import { useSelector } from 'react-redux';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import { select } from '@swf/core/store';

import {
  TXTCLI_Promotions,
  TXTWCL_Future_1,
  TXTWCL_Meetings,
  TXTWCL_My_Bets,
  TXTWCL_breadcrumb
} from './Breadcrumb.messages.json';
import styles from './Breadcrumb.module.scss';

const ALIAS_MAP: Partial<Record<IspSportsAlias, IspSportsAlias>> = {
  sportsPromotion: 'sportsPromotions',
  sportsHistoricalBets: 'sportsMyBets',
  sportsSelfExcludedHistoricalBets: 'sportsSelfExcludedMyBets',
  sportsAnimalRacingRace: 'sportsAnimalRacingMeetings',
  sportsAnimalRacingFutureMarket: 'sportsAnimalRacingFuture',
  sportsCompetitionOutrights: 'sportsSport',
  sportsCompetition: 'sportsSport',
  sportsGolfMatches: 'sportsSport',
  sportsEvent: 'sportsCompetition'
};

const TRANSLATION_MAP: Partial<Record<IspSportsAlias, TranslationTag>> = {
  sportsPromotion: TXTCLI_Promotions,
  sportsHistoricalBets: TXTWCL_My_Bets,
  sportsSelfExcludedHistoricalBets: TXTWCL_My_Bets,
  sportsAnimalRacingRace: TXTWCL_Meetings,
  sportsAnimalRacingFutureMarket: TXTWCL_Future_1
};

interface BreadcrumbProps {
  /** Fallback breadcrumb text to be displayed when default translation is not available */
  fallbackBreadcrumbText?: string;
}

export const Breadcrumb: FC<BreadcrumbProps> = ({ fallbackBreadcrumbText }) => {
  const { formatMessage } = useRegulatoryIntl();
  const urlParams = useSelector(select.sports.getUrlParams);
  const matchingPathnameAlias = useSelector(select.sports.matchPathnameAlias);
  const isMatchingPathnameAliasIspSportsAlias =
    matchingPathnameAlias && isIspSportsAlias(matchingPathnameAlias);

  const alias = isMatchingPathnameAliasIspSportsAlias
    ? ALIAS_MAP[matchingPathnameAlias]
    : undefined;

  const translation =
    alias && isMatchingPathnameAliasIspSportsAlias
      ? TRANSLATION_MAP[matchingPathnameAlias]
      : undefined;

  return (
    <nav
      className={styles.container}
      data-testid="sports-breadcrumbs-widget"
      aria-label={formatMessage(TXTWCL_breadcrumb)}
    >
      <Link alias={alias} values={urlParams}>
        <Icon icon={ChevronMediumLeft} size="xs" />
        {translation ? formatMessage(translation) : fallbackBreadcrumbText}
      </Link>
    </nav>
  );
};
